import React, { useState, useEffect } from "react";
import { ChevronDown, ArrowRight, Quote } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";

const Home = () => {
    const [scrollY, setScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const observers = new Map();
        const elements = document.querySelectorAll("[data-animate]");

        elements.forEach((el, index) => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [index]: true }));
                    }
                },
                { threshold: 0.1 }
            );
            observer.observe(el);
            observers.set(index, observer);
        });

        return () => observers.forEach((observer) => observer.disconnect());
    }, []);

    const stats = [
        { number: "10+", label: "Clients Served" },
        { number: "1+", label: "Years of Innovation" },
        { number: "99.9%", label: "Uptime Guarantee" },
        { number: "24/7", label: "Support Available" },
    ];

    const testimonials = [
        {
            name: "Ravi Sharma",
            role: "Founder, RetailBiz",
            feedback:
                "OneHexa Tech transformed our retail operations with RMS. Efficiency has doubled and customers are happier than ever.",
            // image: "https://randomuser.me/api/portraits/men/32.jpg",
        },
        {
            name: "Ananya Patel",
            role: "HR Manager, TechCorp",
            feedback:
                "Their HRMS system made employee management seamless. Payroll, attendance, and onboarding are now stress-free.",
            // image: "https://randomuser.me/api/portraits/women/44.jpg",
        },
        {
            name: "Mohammed Khan",
            role: "CEO, MediCare",
            feedback:
                "The HIMS software streamlined our hospital workflows. Patient care improved drastically thanks to OneHexa Tech.",
            // image: "https://randomuser.me/api/portraits/men/64.jpg",
        },
        {
            name: "Priya Mehta",
            role: "E-commerce Entrepreneur",
            feedback:
                "Our online sales jumped 3x after migrating to OneHexa Tech’s E-Commerce platform. Highly recommended!",
            // image: "https://randomuser.me/api/portraits/women/68.jpg",
        },
        {
            name: "Arjun Nair",
            role: "Founder, PocketPro",
            feedback:
                "The Pocket Pro app developed by OneHexa Tech exceeded our expectations. Smooth performance and user-friendly design.",
            // image: "https://randomuser.me/api/portraits/men/75.jpg",
        },
        {
            name: "Simran Kaur",
            role: "Teacher, KidsLearn",
            feedback:
                "The Kids Learning app is fantastic! Our students love the interactive design, and learning has become fun.",
            // image: "https://randomuser.me/api/portraits/women/21.jpg",
        },
    ];

    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 600,
        slidesToShow: 4,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 2000,
        arrows: false,
        responsive: [
            { breakpoint: 1024, settings: { slidesToShow: 2 } },
            { breakpoint: 640, settings: { slidesToShow: 1 } },
        ],
    };

    return (
        <>
            <div className="min-h-screen bg-white text-gray-800">
                <Navbar />

                {/* Hero Section */}
                <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
                    <div className="max-w-7xl mx-auto text-center">
                        <div
                            className="animate-fade-in-up"
                            style={{ transform: `translateY(${scrollY * 0.1}px)` }}
                        >
                            <h1 className="text-5xl md:text-7xl font-bold mt-16 text-gray-800 leading-tight">
                                Welcome to
                                <br />
                                <span className="text-green-500 inline-block animate-pulse">
                                    OneHexa Tech
                                </span>
                            </h1>
                            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed capitalize">
                                Powering businesses with{" "}
                                <span className="text-green-500 font-semibold">
                                    next-gen software solutions
                                </span>{" "}
                                for growth, scalability, and innovation.
                            </p>
                        </div>

                        {/* Scroll Indicator */}
                        <div className="animate-bounce mt-16">
                            <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
                        </div>
                    </div>
                </section>

                {/* Stats Section */}
                <section className="py-16 bg-gray-900" data-animate>
                    <div
                        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0]
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 translate-y-10"
                            }`}
                    >
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                            {stats.map((stat, index) => (
                                <div key={index} className="text-white">
                                    <div className="text-4xl md:text-5xl font-bold mb-2 text-green-400 animate-pulse">
                                        {stat.number}
                                    </div>
                                    <div className="text-gray-300 text-lg">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Overview + Products Section */}
                <section className="py-20 bg-gray-50 px-6" data-animate>
                    <div
                        className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible[1]
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 translate-y-10"
                            }`}
                    >
                        {/* Company Overview */}
                        <div className="text-center mb-16">
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                                Why <span className="text-green-500">OneHexa Tech</span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto capitalize">
                                At OneHexa Tech, we deliver{" "}
                                <span className="font-semibold text-green-500">
                                    innovative software solutions
                                </span>{" "}
                                tailored for businesses across industries. From healthcare to e-commerce,
                                our platforms are built to empower organizations with{" "}
                                <span className="font-semibold">
                                    efficiency, scalability, and growth
                                </span>.
                            </p>
                        </div>

                        {/* Products Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                            {[
                                {
                                    title: "HIMS",
                                    desc: "Hospital management system improving workflows and patient care.",
                                },
                                {
                                    title: "RMS",
                                    desc: "Retail management software streamlining sales and inventory.",
                                },
                                {
                                    title: "CRM",
                                    desc: "Customer relationship management for stronger client connections.",
                                },
                                {
                                    title: "HRMS",
                                    desc: "Human resource system simplifying payroll, onboarding & attendance.",
                                },
                                {
                                    title: "E-Commerce",
                                    desc: "Scalable online store solutions to boost digital sales.",
                                },
                                {
                                    title: "School Management System",
                                    desc: "A complete school management solution designed to simplify daily academic and administrative operations."
                                },
                                {
                                    title: "Business Websites",
                                    desc: "Custom websites tailored for brand identity & growth.",
                                },
                            ].map((product, i) => (
                                <div
                                    key={i}
                                    className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 hover:border-green-400 transform hover:scale-105 transition-all duration-300 text-center"
                                >
                                    <h3 className="text-2xl font-bold text-gray-800 mb-3">
                                        {product.title}
                                    </h3>
                                    <p className="text-gray-600">{product.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>


                {/* Testimonial Carousel Section (Commented Out)
                <section className="py-12 sm:py-16 lg:py-20 bg-gray-900" data-animate>
                    ...existing code for testimonials...
                </section>
                */}

                {/* CTA Section */}
                <section className="py-20 bg-gray-900 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                            Ready to Build with{" "}
                            <span className="text-green-400">OneHexa Tech?</span>
                        </h2>
                        <p className="text-xl text-gray-300 mb-8">
                            Partner with us for smarter, faster, and more reliable solutions.
                        </p>
                        <Link to="/contact">
                            <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                                Contact Us
                            </button>
                        </Link>
                    </div>
                </section>

                <Footer />

                {/* Animations */}
                <style jsx>{`
          @keyframes fade-in-up {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fade-in-up {
            animation: fade-in-up 1s ease-out;
          }
        `}</style>
            </div>
        </>
    );
};

export default Home;


// import React, { useState, useEffect } from "react";
// import { ChevronDown, Quote } from "lucide-react";
// import { Link } from "react-router-dom";
// import Navbar from "../layout/Navbar";
// import Footer from "../layout/Footer";
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";
// import Slider from "react-slick";

// const Home = () => {
//     const [scrollY, setScrollY] = useState(0);
//     const [isVisible, setIsVisible] = useState({});
//     const [currentSlide, setCurrentSlide] = useState(0);

//     useEffect(() => {
//         const handleScroll = () => setScrollY(window.scrollY);
//         window.addEventListener("scroll", handleScroll);
//         return () => window.removeEventListener("scroll", handleScroll);
//     }, []);

//     useEffect(() => {
//         const observers = new Map();
//         const elements = document.querySelectorAll("[data-animate]");

//         elements.forEach((el, index) => {
//             const observer = new IntersectionObserver(
//                 ([entry]) => {
//                     if (entry.isIntersecting) {
//                         setIsVisible((prev) => ({ ...prev, [index]: true }));
//                     }
//                 },
//                 { threshold: 0.1 }
//             );
//             observer.observe(el);
//             observers.set(index, observer);
//         });

//         return () => observers.forEach((observer) => observer.disconnect());
//     }, []);

//     const stats = [
//         { number: "10+", label: "Clients Served" },
//         { number: "1+", label: "Years of Innovation" },
//         { number: "99.9%", label: "Uptime Guarantee" },
//         { number: "24/7", label: "Support Available" },
//     ];

//     const testimonials = [
//         {
//             name: "Ravi Sharma",
//             role: "Founder, RetailBiz",
//             feedback:
//                 "OneHexa Tech transformed our retail operations with RMS. Efficiency has doubled and customers are happier than ever.",
//             image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
//         },
//         {
//             name: "Ananya Patel",
//             role: "HR Manager, TechCorp",
//             feedback:
//                 "Their HRMS system made employee management seamless. Payroll, attendance, and onboarding are now stress-free.",
//             image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
//         },
//         {
//             name: "Mohammed Khan",
//             role: "CEO, MediCare",
//             feedback:
//                 "The HIMS software streamlined our hospital workflows. Patient care improved drastically thanks to OneHexa Tech.",
//             image: "https://images.unsplash.com/photo-1556157382-97eda2d62296?w=400&h=400&fit=crop",
//         },
//         {
//             name: "Priya Mehta",
//             role: "E-commerce Entrepreneur",
//             feedback:
//                 "Our online sales jumped 3x after migrating to OneHexa Tech's E-Commerce platform. Highly recommended!",
//             image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop",
//         },
//         {
//             name: "Arjun Nair",
//             role: "Founder, PocketPro",
//             feedback:
//                 "The Pocket Pro app developed by OneHexa Tech exceeded our expectations. Smooth performance and user-friendly design.",
//             image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop",
//         },
//         {
//             name: "Simran Kaur",
//             role: "Teacher, KidsLearn",
//             feedback:
//                 "The Kids Learning app is fantastic! Our students love the interactive design, and learning has become fun.",
//             image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=400&fit=crop",
//         },
//     ];

//     const sliderSettings = {
//         dots: true,
//         infinite: true,
//         speed: 600,
//         slidesToShow: 4,
//         slidesToScroll: 1,
//         autoplay: true,
//         autoplaySpeed: 2000,
//         arrows: false,
//         responsive: [
//             { breakpoint: 1024, settings: { slidesToShow: 2 } },
//             { breakpoint: 640, settings: { slidesToShow: 1 } },
//         ],
//     };

//     return (
//         <>
//             <div className="min-h-screen bg-white text-gray-800">
//                 <Navbar />

//                 {/* Hero Section with Background Image */}
//                 <section className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
//                     {/* Background Image with Overlay */}
//                     <div className="absolute inset-0 z-0">
//                         <img
//                             src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop"
//                             alt="Technology Background"
//                             className="w-full h-full object-cover"
//                         />
//                         <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-white/10 to-black"></div>
//                     </div>

//                     <div className="relative z-10 max-w-7xl mx-auto text-center">
//                         <div
//                             className="animate-fade-in-up"
//                             style={{ transform: `translateY(${scrollY * 0.1}px)` }}
//                         >
//                             <h1 className="text-5xl md:text-7xl font-bold mt-16 text-white/50 leading-tight">
//                                 Welcome to
//                                 <br />
//                                 <span className="text-green-500 inline-block animate-pulse">
//                                     OneHexa Tech
//                                 </span>
//                             </h1>
//                             <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto leading-relaxed capitalize">
//                                 Powering businesses with{" "}
//                                 <span className="text-green-500 font-semibold">
//                                     next-gen software solutions
//                                 </span>{" "}
//                                 for growth, scalability, and innovation.
//                             </p>
//                         </div>

//                         {/* Scroll Indicator */}
//                         <div className="animate-bounce mt-16">
//                             <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
//                         </div>
//                     </div>
//                 </section>

//                 {/* Stats Section with Tech Background */}
//                 <section className="relative py-16 overflow-hidden" data-animate>
//                     <div className="absolute inset-0 z-0">
//                         <img
//                             src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&h=600&fit=crop"
//                             alt="Data Center"
//                             className="w-full h-full object-cover"
//                         />
//                         <div className="absolute inset-0 bg-gray-900/90"></div>
//                     </div>

//                     <div
//                         className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0]
//                             ? "opacity-100 translate-y-0"
//                             : "opacity-0 translate-y-10"
//                             }`}
//                     >
//                         <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
//                             {stats.map((stat, index) => (
//                                 <div key={index} className="text-white">
//                                     <div className="text-4xl md:text-5xl font-bold mb-2 text-green-400 animate-pulse">
//                                         {stat.number}
//                                     </div>
//                                     <div className="text-gray-300 text-lg">{stat.label}</div>
//                                 </div>
//                             ))}
//                         </div>
//                     </div>
//                 </section>

//                 {/* Overview + Products Section */}
//                 <section className="py-20 bg-gray-50 px-6" data-animate>
//                     <div
//                         className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible[1]
//                             ? "opacity-100 translate-y-0"
//                             : "opacity-0 translate-y-10"
//                             }`}
//                     >
//                         {/* Company Overview with Side Image */}
//                         <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
//                             <div>
//                                 <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
//                                     Why <span className="text-green-500">OneHexa Tech</span>
//                                 </h2>
//                                 <p className="text-xl text-gray-600 capitalize leading-relaxed">
//                                     At OneHexa Tech, we deliver{" "}
//                                     <span className="font-semibold text-green-500">
//                                         innovative software solutions
//                                     </span>{" "}
//                                     tailored for businesses across industries. From healthcare to e-commerce,
//                                     our platforms are built to empower organizations with{" "}
//                                     <span className="font-semibold">
//                                         efficiency, scalability, and growth
//                                     </span>.
//                                 </p>
//                             </div>
//                             <div className="relative">
//                                 <div className="absolute inset-0 bg-green-500/20 rounded-2xl blur-2xl"></div>
//                                 <img
//                                     src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
//                                     alt="Team Collaboration"
//                                     className="relative rounded-2xl shadow-2xl w-full h-auto"
//                                 />
//                             </div>
//                         </div>

//                         {/* Products Grid with Icons */}
//                         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
//                             {[
//                                 {
//                                     title: "HIMS",
//                                     desc: "Hospital management system improving workflows and patient care.",
//                                     img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=300&fit=crop"
//                                 },
//                                 {
//                                     title: "RMS",
//                                     desc: "Retail management software streamlining sales and inventory.",
//                                     img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop"
//                                 },
//                                 {
//                                     title: "CRM",
//                                     desc: "Customer relationship management for stronger client connections.",
//                                     img: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=300&fit=crop"
//                                 },
//                                 {
//                                     title: "HRMS",
//                                     desc: "Human resource system simplifying payroll, onboarding & attendance.",
//                                     img: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop"
//                                 },
//                                 {
//                                     title: "E-Commerce",
//                                     desc: "Scalable online store solutions to boost digital sales.",
//                                     img: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=400&h=300&fit=crop"
//                                 },
//                                 {
//                                     title: "School Management System",
//                                     desc: "Manage all Students, Teachers",
//                                     img: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=300&fit=crop"
//                                 },
//                                 {
//                                     title: "Business Websites",
//                                     desc: "Custom websites tailored for brand identity & growth.",
//                                     img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop"
//                                 },
//                             ].map((product, i) => (
//                                 <div
//                                     key={i}
//                                     className="bg-white rounded-2xl shadow-lg border border-gray-200 hover:border-green-400 transform hover:scale-105 transition-all duration-300 overflow-hidden group"
//                                 >
//                                     <div className="relative h-48 overflow-hidden">
//                                         <img
//                                             src={product.img}
//                                             alt={product.title}
//                                             className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
//                                         />
//                                         <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
//                                     </div>
//                                     <div className="p-8">
//                                         <h3 className="text-2xl font-bold text-gray-800 mb-3">
//                                             {product.title}
//                                         </h3>
//                                         <p className="text-gray-600">{product.desc}</p>
//                                     </div>
//                                 </div>
//                             ))}
//                         </div>
//                     </div>
//                 </section>


//                 {/* Testimonial Carousel Section */}
//                 <section className="py-12 sm:py-16 lg:py-20 bg-gray-900" data-animate>
//                     <div
//                         className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[2] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
//                             }`}
//                     >
//                         <div className="text-center mb-12 sm:mb-16">
//                             <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 sm:mb-4 px-4">
//                                 What Our <span className="text-green-400">Clients Say</span>
//                             </h2>
//                             <p className="text-sm sm:text-base lg:text-lg text-gray-300 max-w-xl lg:max-w-2xl mx-auto px-4">
//                                 Discover how our solutions have transformed businesses across various industries
//                             </p>
//                         </div>

//                         <div className="relative">
//                             {/* Main Testimonial Display */}
//                             <div className="grid items-center gap-6 sm:gap-8 md:grid-cols-5">
//                                 {/* Image Section */}
//                                 <div className="md:col-span-2">
//                                     <div className="relative group">
//                                         <div className="absolute inset-0 transition-opacity duration-300 opacity-75 bg-gradient-to-r from-green-600 to-green-500 rounded-2xl blur-lg group-hover:opacity-100"></div>
//                                         <img
//                                             src={testimonials[currentSlide].image}
//                                             alt={testimonials[currentSlide].name}
//                                             className="relative object-cover w-full transition-transform duration-500 transform shadow-2xl h-64 sm:h-80 lg:h-96 rounded-2xl group-hover:scale-105"
//                                         />
//                                     </div>
//                                 </div>

//                                 {/* Content Section */}
//                                 <div className="p-4 sm:p-6 lg:p-8 md:col-span-3">
//                                     <div className="relative text-start">
//                                         <Quote className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 mb-6 mx-auto text-green-500 opacity-50" />

//                                         {/* Feedback text */}
//                                         <p className="mb-8 text-xl sm:text-2xl lg:text-2xl font-medium leading-relaxed text-gray-300">
//                                             {testimonials[currentSlide].feedback}
//                                         </p>

//                                         {/* Name + Role */}
//                                         <div className="space-y-2">
//                                             <h3 className="text-2xl md:text-2xl font-semibold text-white">
//                                                 {testimonials[currentSlide].name}
//                                             </h3>
//                                             <p className="text-lg text-green-400">
//                                                 {testimonials[currentSlide].role}
//                                             </p>
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* Navigation Controls */}
//                             <div className="flex justify-center mt-8 sm:mt-12 space-x-3 sm:space-x-4">
//                                 {testimonials.map((_, index) => (
//                                     <button
//                                         key={index}
//                                         onClick={() => setCurrentSlide(index)}
//                                         className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${index === currentSlide
//                                             ? "bg-green-500 w-6 sm:w-8"
//                                             : "bg-gray-600 hover:bg-gray-500"
//                                             }`}
//                                         aria-label={`Go to testimonial ${index + 1}`}
//                                     />
//                                 ))}
//                             </div>

//                             {/* Arrow Navigation */}
//                             <button
//                                 onClick={() => setCurrentSlide(currentSlide === 0 ? testimonials.length - 1 : currentSlide - 1)}
//                                 className="absolute left-2 sm:left-0 top-1/2 p-2 sm:p-3 transition-all duration-300 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm"
//                                 aria-label="Previous testimonial"
//                             >
//                                 <svg
//                                     className="w-5 h-5 sm:w-6 sm:h-6 text-white"
//                                     fill="none"
//                                     stroke="currentColor"
//                                     viewBox="0 0 24 24"
//                                 >
//                                     <path
//                                         strokeLinecap="round"
//                                         strokeLinejoin="round"
//                                         strokeWidth={2}
//                                         d="M15 19l-7-7 7-7"
//                                     />
//                                 </svg>
//                             </button>
//                             <button
//                                 onClick={() => setCurrentSlide(currentSlide === testimonials.length - 1 ? 0 : currentSlide + 1)}
//                                 className="absolute right-2 sm:right-0 top-1/2 p-2 sm:p-3 transition-all duration-300 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm"
//                                 aria-label="Next testimonial"
//                             >
//                                 <svg
//                                     className="w-5 h-5 sm:w-6 sm:h-6 text-white"
//                                     fill="none"
//                                     stroke="currentColor"
//                                     viewBox="0 0 24 24"
//                                 >
//                                     <path
//                                         strokeLinecap="round"
//                                         strokeLinejoin="round"
//                                         strokeWidth={2}
//                                         d="M9 5l7 7-7 7"
//                                     />
//                                 </svg>
//                             </button>
//                         </div>
//                     </div>
//                 </section>

//                 {/* CTA Section with Background */}
//                 <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-animate>
//                     <div className="absolute inset-0 z-0">
//                         <img
//                             src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1920&h=600&fit=crop"
//                             alt="Office Team"
//                             className="w-full h-full object-cover"
//                         />
//                         <div className="absolute inset-0 bg-gray-900/85"></div>
//                     </div>

//                     <div
//                         className={`relative z-10 max-w-4xl mx-auto text-center transition-all duration-1000 delay-300 ${isVisible[3]
//                             ? "opacity-100 translate-y-0"
//                             : "opacity-0 translate-y-10"
//                             }`}
//                     >
//                         <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
//                             Ready to Build with{" "}
//                             <span className="text-green-400">OneHexa Tech?</span>
//                         </h2>
//                         <p className="text-xl text-gray-300 mb-8">
//                             Partner with us for smarter, faster, and more reliable solutions.
//                         </p>
//                         <Link to="/contact">
//                             <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
//                                 Contact Us
//                             </button>
//                         </Link>
//                     </div>
//                 </section>

//                 <Footer />

//                 {/* Animations */}
//                 <style jsx>{`
//           @keyframes fade-in-up {
//             from {
//               opacity: 0;
//               transform: translateY(30px);
//             }
//             to {
//               opacity: 1;
//               transform: translateY(0);
//             }
//           }
//           .animate-fade-in-up {
//             animation: fade-in-up 1s ease-out;
//           }
//         `}</style>
//             </div>
//         </>
//     );
// };

// export default Home;